import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import FormContainer from '../components/FormContainer';
import { listProductDetails, updateProduct } from '../actions/productActions';
import { PRODUCT_UPDATE_RESET } from '../constants/productConstants';
import axios from 'axios';

const ProductEditScreen = () => {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [price, setPrice] = useState(0);
  const [image, setImage] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [countInStock, setCountInStock] = useState(0);
  const [description, setDescription] = useState('');
  const [specifications, setSpecifications] = useState('');
  const [certifications, setCertifications] = useState('');
  const [bulkPricing, setBulkPricing] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Add a new row to bulk pricing
  const addBulkPricingRow = () => {
    setBulkPricing([...bulkPricing, { quantity: 10, price: price * 0.9 }]);
  };

  // Update bulk pricing row
  const updateBulkPricing = (index, field, value) => {
    const updatedPricing = [...bulkPricing];
    updatedPricing[index][field] = value;
    setBulkPricing(updatedPricing);
  };

  // Remove bulk pricing row
  const removeBulkPricingRow = (index) => {
    const updatedPricing = [...bulkPricing];
    updatedPricing.splice(index, 1);
    setBulkPricing(updatedPricing);
  };

  const productDetails = useSelector((state) => state.productDetails);
  const { loading, error, product } = productDetails;

  const productUpdate = useSelector((state) => state.productUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = productUpdate;

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: PRODUCT_UPDATE_RESET });
      navigate('/admin/productlist');
    } else {
      if (!product || !product.name || product._id !== productId) {
        dispatch(listProductDetails(productId));
      } else {
        setName(product.name);
        setPrice(product.price);
        setImage(product.image);
        setBrand(product.brand);
        setCategory(product.category);
        setCountInStock(product.countInStock);
        setDescription(product.description);
        setSpecifications(product.specifications || '');
        setCertifications(product.certifications || '');
        setBulkPricing(product.bulkPricing || []);
      }
    }
  }, [dispatch, navigate, productId, product, successUpdate]);

  const uploadFileHandler = async (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('image', file);
    setUploading(true);
    setUploadError('');

    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const { data } = await axios.post('/api/upload', formData, config);

      setImage(data);
      setUploading(false);
    } catch (error) {
      console.error(error);
      setUploadError('Image upload failed. Please try again.');
      setUploading(false);
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(
      updateProduct({
        _id: productId,
        name,
        price,
        image,
        brand,
        category,
        description,
        countInStock,
        specifications,
        certifications,
        bulkPricing,
      })
    );
  };

  const categories = [
    'Eye Protection',
    'Hand Protection',
    'Respiratory Protection',
    'Hearing Protection',
    'Foot Protection',
    'Disposables',
    'Chemical Suits',
    'Fire Protection',
    'Laboratory Equipment',
    'Scientific Instruments',
    'Other',
  ];

  return (
    <>
      <Link to='/admin/productlist' className='flex items-center text-primary mb-4'>
        <i className='fas fa-arrow-left mr-1'></i> Back to Products
      </Link>
      <FormContainer>
        <h1 className='text-2xl font-semibold mb-6'>Edit Product</h1>
        {loadingUpdate && <Loader />}
        {errorUpdate && <Message variant='danger'>{errorUpdate}</Message>}
        {loading ? (
          <Loader />
        ) : error ? (
          <Message variant='danger'>{error}</Message>
        ) : (
          <form onSubmit={submitHandler} className='space-y-6'>
            <div>
              <label htmlFor='name' className='block text-gray-700 font-medium mb-2'>
                Name
              </label>
              <input
                type='text'
                id='name'
                placeholder='Enter product name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              />
            </div>

            <div>
              <label htmlFor='price' className='block text-gray-700 font-medium mb-2'>
                Price (KES)
              </label>
              <input
                type='number'
                id='price'
                placeholder='Enter price'
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              />
            </div>

            <div>
              <label htmlFor='image' className='block text-gray-700 font-medium mb-2'>
                Image
              </label>
              <input
                type='text'
                id='image'
                placeholder='Enter image URL'
                value={image}
                onChange={(e) => setImage(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
              />
              <input
                type='file'
                id='image-file'
                onChange={uploadFileHandler}
                className='mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-light file:text-primary hover:file:bg-primary-light'
              />
              {uploading && <Loader />}
              {uploadError && <Message variant='danger'>{uploadError}</Message>}
            </div>

            <div>
              <label htmlFor='brand' className='block text-gray-700 font-medium mb-2'>
                Brand
              </label>
              <input
                type='text'
                id='brand'
                placeholder='Enter brand name'
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              />
            </div>

            <div>
              <label htmlFor='countInStock' className='block text-gray-700 font-medium mb-2'>
                Count In Stock
              </label>
              <input
                type='number'
                id='countInStock'
                placeholder='Enter stock quantity'
                value={countInStock}
                onChange={(e) => setCountInStock(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              />
            </div>

            <div>
              <label htmlFor='category' className='block text-gray-700 font-medium mb-2'>
                Category
              </label>
              <select
                id='category'
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              >
                <option value=''>Select Category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor='description' className='block text-gray-700 font-medium mb-2'>
                Description
              </label>
              <textarea
                id='description'
                rows='4'
                placeholder='Enter product description'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                required
              ></textarea>
            </div>

            <div>
              <label htmlFor='specifications' className='block text-gray-700 font-medium mb-2'>
                Specifications
              </label>
              <textarea
                id='specifications'
                rows='4'
                placeholder='Enter product specifications'
                value={specifications}
                onChange={(e) => setSpecifications(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
              ></textarea>
              <p className='mt-1 text-sm text-gray-500'>
                Enter specifications in a format like: "Material: Polycarbonate, Weight: 100g"
              </p>
            </div>

            <div>
              <label htmlFor='certifications' className='block text-gray-700 font-medium mb-2'>
                Certifications
              </label>
              <textarea
                id='certifications'
                rows='3'
                placeholder='Enter product certifications'
                value={certifications}
                onChange={(e) => setCertifications(e.target.value)}
                className='w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
              ></textarea>
              <p className='mt-1 text-sm text-gray-500'>
                Enter certifications like: "ISO 9001, KEBS Certified, CE Mark"
              </p>
            </div>

            <div>
              <div className='flex justify-between items-center mb-2'>
                <label className='block text-gray-700 font-medium'>Bulk Pricing</label>
                <button
                  type='button'
                  onClick={addBulkPricingRow}
                  className='text-primary hover:text-primary-dark'
                >
                  <i className='fas fa-plus-circle mr-1'></i> Add Tier
                </button>
              </div>

              {bulkPricing.length > 0 ? (
                <div className='border border-gray-300 rounded-md overflow-hidden'>
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
                          Minimum Quantity
                        </th>
                        <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
                          Price Per Unit (KES)
                        </th>
                        <th className='px-4 py-2 text-xs font-medium text-gray-500 uppercase text-center'>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                      {bulkPricing.map((tier, index) => (
                        <tr key={index}>
                          <td className='px-4 py-2'>
                            <input
                              type='number'
                              value={tier.quantity}
                              onChange={(e) =>
                                updateBulkPricing(index, 'quantity', Number(e.target.value))
                              }
                              className='w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                              min='2'
                            />
                          </td>
                          <td className='px-4 py-2'>
                            <input
                              type='number'
                              value={tier.price}
                              onChange={(e) =>
                                updateBulkPricing(index, 'price', Number(e.target.value))
                              }
                              className='w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                              min='0'
                            />
                          </td>
                          <td className='px-4 py-2 text-center'>
                            <button
                              type='button'
                              onClick={() => removeBulkPricingRow(index)}
                              className='text-red-600 hover:text-red-800'
                            >
                              <i className='fas fa-trash'></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className='text-sm text-gray-500'>
                  No bulk pricing tiers added. Click "Add Tier" to create volume discount pricing.
                </p>
              )}
            </div>

            <button
              type='submit'
              className='w-full bg-primary hover:bg-primary-dark text-white font-medium py-2 rounded-md'
            >
              Update Product
            </button>
          </form>
        )}
      </FormContainer>
    </>
  );
};

export default ProductEditScreen;
